import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch } from "react-redux";
import { GetFundInvestors, GetOnboardedInvestors } from "../../Redux/Actions/strategyService";
import { postUser } from "../../Redux/Actions/userStatusService";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import customSuccessAlert from "../../Alerts/customSuccessAlert";
import customFailAlert from "../../Alerts/customFailAlert";
import Loader from "../Loader/Index";

// Import the new components
import FundDetailsTab from "./FundDetailsTab";
import FundAnalyticsTab from "./FundAnalyticsTab";
import InvestorStatementsTab from "./InvestorStatementsTab";
import '../../Styles/AccountingStyles.css'

const FundDetails = ({ fund, fundId, firmId, onBack, firmName, initialTab = 0 }) => {
  const [mainTab, setMainTab] = useState(initialTab);
  const dispatch = useDispatch();
  const [investors, setInvestors] = useState([]);
  const [investorsLoading, setInvestorsLoading] = useState(false);
  // Modal state for Send Invite Link
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  // Add state for tab selection in Manage Investors
  const [manageTab, setManageTab] = useState('notOnboarded'); // 'notOnboarded' or 'onboarded'
  // Add state for onboarded and not onboarded investors
  const [notOnboardedInvestors, setNotOnboardedInvestors] = useState([]);
  const [onboardedInvestors, setOnboardedInvestors] = useState([]);
  // Add state to track the selected investor for invite
  const [selectedInviteInvestor, setSelectedInviteInvestor] = useState(null);
  // Add state for Add User modal
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserRole, setAddUserRole] = useState('');
  // Add state for users in Manage Users section, per investor
  const [investorUsersMap, setInvestorUsersMap] = useState({});
  const [manageUsersList, setManageUsersList] = useState([]);
  // Add state for editing user
  const [editingUserIdx, setEditingUserIdx] = useState(null);
  const [editUserRole, setEditUserRole] = useState('');
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  // Add state for modal step process
  const [addUserStep, setAddUserStep] = useState(1); // 1: empty, 2: add modal open, 3: add modal filled, 4: user list, 5: edit modal open
  // Add state for remove user modal
  const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
  const [removingUserIdx, setRemovingUserIdx] = useState(null);
  // Add state for managing users
  const [manageUsersInvestor, setManageUsersInvestor] = useState(null);
  // Add state for loading
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenInviteModal = (email, name) => {
    setInviteName(name);
    setSelectedInviteInvestor(email); // Track which investor is being invited
    setInviteModalOpen(true);
  };
  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
    setInviteName('');
    setSelectedInviteInvestor(null);
    setIsLoading(false); // Ensure loading is also stopped
  };

  // Yup validation schema
  const InviteSchema = Yup.object().shape({
    inviteEmail: Yup.string()
      .email('Please enter a valid email address')
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Please enter a valid email address'
      )
      .min(5, 'Email must be at least 5 characters')
      .max(254, 'Email must be less than 254 characters')
      .required('Email is required'),
    accountType: Yup.string().oneOf(['Individual', 'Corporate'], 'Select account type').required('Account type is required'),
  });

  // Update handleSendLink to accept email and accountType from Formik
  const handleSendLink = async (inviteEmail, accountType) => {
    setIsLoading(true);
    
    // Find the investor object for the selected email
    const movedInvestor = notOnboardedInvestors.find(
      inv => inv.email === selectedInviteInvestor
    );
    
    // Prepare payload
    const payload = {
      email: inviteEmail,
      role: 'INVESTOR',
      investmentAccountType: 'FUND',
      investorType: accountType === 'Individual' ? 'INDIVIDUAL' : 'CORPORATE',
      accountingFirmAssignment: {
        accountingFirmId: firmId,
        investorId: movedInvestor
          ? movedInvestor.InvestorID || movedInvestor.InvestorId || movedInvestor.id
          : ''
      }
    };
    
    try {
      const response = await dispatch(postUser(payload)).unwrap();
      setInviteModalOpen(false);
      // Check if the response has a status code or if we need to check the response structure
      if (response && (response.statusCode === 201 || response.status === 201 || response.success)) {
        // Stop loading immediately for success
        setIsLoading(false);
        
        // Show success modal
        await customSuccessAlert("Invite link sent successfully!");
        
        // Close modal immediately after success popup
        setInviteModalOpen(false);
        setInviteName('');
        setSelectedInviteInvestor(null);
        
        // Move the correct investor from notOnboarded to onboarded by matching unique id
        if (movedInvestor) {
          setNotOnboardedInvestors(prev =>
            prev.filter(
              inv =>
                (inv.InvestorID || inv.InvestorId || inv.id) !==
                (movedInvestor.InvestorID || movedInvestor.InvestorId || movedInvestor.id)
            )
          );
          setOnboardedInvestors(prev => {
            // Prevent duplicates
            if (
              !prev.some(
                inv =>
                  (inv.InvestorID || inv.InvestorId || inv.id) ===
                  (movedInvestor.InvestorID || movedInvestor.InvestorId || movedInvestor.id)
              )
            ) {
              return [
                ...prev,
                { ...movedInvestor, onboarded: true }
              ];
            }
            return prev;
          });
        }
        
        setManageTab('onboarded');
        
        // Fallback: ensure modal closes after a short delay
        setTimeout(() => {
          if (inviteModalOpen) {
            setInviteModalOpen(false);
            setInviteName('');
            setSelectedInviteInvestor(null);
          }
        }, 1000);
      } else {
        // Stop loading immediately for failure
        setIsLoading(false);
        
        // Show failure modal for unexpected response
        await customFailAlert("This Email are not Linked with this Fund Investor");
        
        // Close modal immediately after failure popup
        setInviteModalOpen(false);
        setInviteName('');
        setSelectedInviteInvestor(null);
        
        // Fallback: ensure modal closes after a short delay
        setTimeout(() => {
          if (inviteModalOpen) {

            setInviteModalOpen(false);
            setInviteName('');
            setSelectedInviteInvestor(null);
          }
        }, 1000);
        
        return; // Exit early to prevent finally block from running
      }
    } catch (error) {
      // Stop loading immediately for error
      setIsLoading(false);
      
      // Show failure modal for error
      console.error('Error sending invite:', error);
      const errorMessage = error?.message || error?.data?.message || "Failed to send invite link. Please try again.";
      await customFailAlert(errorMessage);
      
      // Close modal immediately after error popup
      setInviteModalOpen(false);
      setInviteName('');
      setSelectedInviteInvestor(null);
      
      // Fallback: ensure modal closes after a short delay
      setTimeout(() => {
        if (inviteModalOpen) {
          setInviteModalOpen(false);
          setInviteName('');
          setSelectedInviteInvestor(null);
        }
      }, 1000);
    } finally {
      // Only set loading to false if we haven't already done so
      if (isLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (firmId && fundId) {
      setInvestorsLoading(true);
      Promise.all([
        dispatch(GetFundInvestors({ id: firmId, fundId })).unwrap(),
        dispatch(GetOnboardedInvestors(firmId)).unwrap()
      ])
        .then(([investorsRes, onboardedRes]) => {
          const investors = investorsRes?.data || [];
          const onboarded = onboardedRes?.data || [];
          const onboardedIds = new Set(onboarded.map(o => String(o.investorId)));
          const onboardedList = [];
          const notOnboardedList = [];
          investors.forEach(inv => {
            if (onboardedIds.has(String(inv.InvestorID))) {
              onboardedList.push({
                ...inv,
                onboarded: true,
                name: inv.InvestorName,
                email: inv.InvestorEmail || ""
              });
            } else {
              notOnboardedList.push({
                ...inv,
                onboarded: false,
                name: inv.InvestorName,
                email: inv.InvestorEmail || ""
              });
            }
          });
          setOnboardedInvestors(onboardedList);
          setNotOnboardedInvestors(notOnboardedList);
        })
        .catch(() => {
          setOnboardedInvestors([]);
          setNotOnboardedInvestors([]);
        })
        .finally(() => setInvestorsLoading(false));
    }
  }, [dispatch, firmId, fundId]);

  // Add effect to clear Manage Users selection when tab changes
  useEffect(() => {
    if (manageTab !== 'onboarded') {
      setManageUsersInvestor(null);
    }
  }, [manageTab]);

  // Add effect to clear Manage Users selection and modals when tab or investor changes
  useEffect(() => {
    setAddUserModalOpen(false);
    setEditUserModalOpen(false);
    setAddUserEmail('');
    setAddUserRole('');
    setEditingUserIdx(null);
    setEditUserRole('');
    // If this investor has no users, start with empty list
    if (manageUsersInvestor) {
      setManageUsersList(investorUsersMap[manageUsersInvestor.email] || []);
    } else {
      setManageUsersList([]);
    }
  }, [manageTab, manageUsersInvestor]);

  // Add effect to reset Manage Investors tab state and set manageTab to 'notOnboarded' when mainTab changes away from 3
  useEffect(() => {
    if (mainTab !== 3) {
      setManageUsersInvestor(null);
      setManageUsersList([]);
      setAddUserModalOpen(false);
      setEditUserModalOpen(false);
      setAddUserEmail('');
      setAddUserRole('');
      setEditingUserIdx(null);
      setEditUserRole('');
      setManageTab('notOnboarded');
    }
  }, [mainTab]);


  if (!fund) {
    return (
      <Box className="container">
        <Typography>Fund not found</Typography>
        <Button onClick={onBack}>Go Back</Button>
      </Box>
    );
  }

  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
  };

  // When adding or editing a user, update the user list for the current investor
  const handleAddUser = () => {
    if (addUserEmail && addUserRole && manageUsersInvestor) {
      const newList = [
        ...manageUsersList,
        {
          name: addUserEmail ? addUserEmail.split('@')[0].replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'New User',
          email: addUserEmail,
          role: addUserRole.charAt(0).toUpperCase() + addUserRole.slice(1),
          status: 'Active',
        }
      ];
      setManageUsersList(newList);
      setInvestorUsersMap(prev => ({ ...prev, [manageUsersInvestor.email]: newList }));
      setAddUserModalOpen(false);
      setAddUserEmail('');
      setAddUserRole('');
    }
  };
  const handleUpdateUser = () => {
    if (editUserRole && editingUserIdx !== null && manageUsersInvestor) {
      const newList = manageUsersList.map((u, i) => i === editingUserIdx ? { ...u, role: editUserRole.charAt(0).toUpperCase() + editUserRole.slice(1) } : u);
      setManageUsersList(newList);
      setInvestorUsersMap(prev => ({ ...prev, [manageUsersInvestor.email]: newList }));
      setEditUserModalOpen(false);
      setEditingUserIdx(null);
      setEditUserRole('');
    }
  };

  return (
    <Box className="container" sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", p: 2 }}>
      {/* Header with back button and fund name */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 1, mt:-5 }}>
        <IconButton onClick={onBack} sx={{ mr: 1, p: 0 }}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'normal' }}>
          {fund.name}
        </Typography>
      </Box>
      
      {/* White card container for tabs */}
      <Paper 
        elevation={1} 
        sx={{ 
          borderRadius: '8px', 
          overflow: 'hidden',
          mb: 3,
          p:2
        }}
      >
        <Tabs 
          value={mainTab} 
          onChange={handleMainTabChange}
          TabIndicatorProps={{
            style: {
              backgroundColor: '#1976d2',
            }
          }}
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'normal',
              color: 'text.secondary',
              fontSize: '0.95rem',
              padding: '16px 24px',
              minWidth: 'auto',
            },
            '& .Mui-selected': {
              color: '#1976d2',
              fontWeight: 'medium'
            }
          }}
        >
          <Tab label="Details" />
          {/* <Tab label="Analytics" /> */}
          <Tab label="Investor Statements" />
          <Tab label="Manage Investors" />
        </Tabs>
      </Paper>
      
      <div elevation={1} sx={{ borderRadius: '8px', p: 3 }}>
        {mainTab === 0 && <FundDetailsTab
          fund={fund}
          id={firmId}
          fundId={fundId}
          investors={investors}
        />}
        {/* {mainTab === 1 && <FundAnalyticsTab fund={fund} investors={investors} fundId={fundId} firmId={firmId} />} */}
        {mainTab === 1 && <InvestorStatementsTab fund={fund} investors={investors} fundId={fundId} firmId={firmId} />}
        {mainTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
            {/* Investor List Card */}
            <Box sx={{ p: 0, maxWidth: 700, minWidth: 500, ml: 0 }}>
              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #eee', p: 3 }}>
                <Typography sx={{ color: '#757575', fontWeight: 500, fontSize: '16px', mb: 2 }}>
                  Investor List
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: manageTab === 'notOnboarded' ? '#1976d2' : '#eee',
                      color: manageTab === 'notOnboarded' ? '#1976d2' : '#757575',
                      fontWeight: 500,
                      fontSize: '15px',
                      px: 2.5,
                      py: 0.5,
                      borderRadius: '6px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      background: manageTab === 'notOnboarded' ? '#f5faff' : '#fff',
                      '&:hover': {
                        borderColor: '#1976d2',
                        background: '#f5faff',
                      },
                    }}
                    disableElevation
                    onClick={() => setManageTab('notOnboarded')}
                  >
                    Not Onboarded
                  </Button>
                  <Button
                    variant="outlined"
                    disabled
                    sx={{
                      borderColor: manageTab === 'onboarded' ? '#1976d2' : '#eee',
                      color: manageTab === 'onboarded' ? '#1976d2' : '#757575',
                      fontWeight: 500,
                      fontSize: '15px',
                      px: 2.5,
                      py: 0.5,
                      borderRadius: '6px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      background: manageTab === 'onboarded' ? '#f5faff' : '#fff',
                      '&:hover': {
                        borderColor: '#1976d2',
                        background: '#f5faff',
                      },
                    }}
                    disableElevation
                    onClick={() => setManageTab('onboarded')}
                  >
                    Onboarded
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
                  <Typography variant="body2" sx={{ flex: 1, color: '#757575', fontWeight: 500, fontSize: '14px' }}>
                    Investor Name
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 0, color: '#757575', fontWeight: 500, fontSize: '14px', minWidth: 110, textAlign: 'right' }}>
                    Action
                  </Typography>
                </Box>
                <hr style={{ margin: 0, border: 0, borderTop: '1px solid #eee' }} />
                {(manageTab === 'notOnboarded' ? notOnboardedInvestors : onboardedInvestors).map((inv, idx) => {
                  let initials = inv.name.split(' ').map(n => n[0]).join('').toUpperCase();
                  initials = initials.slice(0, 3); // Only first 3 letters
                  const isSelected = manageTab === 'onboarded' && manageUsersInvestor && manageUsersInvestor.email === inv.email;
                  return (
                    <Box key={inv.name + idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, bgcolor: isSelected ? '#f8f5fc' : 'transparent', borderRadius: isSelected ? '6px' : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          minHeight: 40,
                          borderRadius: '50%',
                          backgroundColor: '#e0e3e7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}>
                          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%', color: '#757575', fontWeight: 600, fontSize: '12px' }}>{initials}</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '14px', color: '#212121' }}>{inv.name}</Typography>
                      </Box>
                      <Box sx={{ minWidth: 110, textAlign: 'right' }}>
                        {manageTab === 'notOnboarded' ? (
                          <Button
                            variant="text"
                            sx={{ color: '#1344EB', fontWeight: 500, fontSize: '15px', textTransform: 'none', p: 0, minWidth: 0, textDecoration: 'underline' }}
                            onClick={() => handleOpenInviteModal(inv.email, inv.name)}
                          >
                            Send Invite Link
                          </Button>
                        ) : (
                          <Button
                            variant="text"
                            sx={{ color: '#1344EB', fontWeight: 500, fontSize: '15px', textTransform: 'none', p: 0, minWidth: 0, textDecoration: 'underline' }}
                            onClick={() => setManageUsersInvestor(inv)}
                          >
                            Manage Users
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {/* Manage Users Panel (only for Onboarded tab and only when Manage Users is clicked) */}
            {manageTab === 'onboarded' && manageUsersInvestor && (
              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #eee', p: 4, minWidth: 440, maxWidth: 520, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {manageUsersList.length === 0 ? (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#212121', textAlign: 'left', alignSelf: 'flex-start' }}>Manage Users</Typography>
                    <Typography sx={{ color: '#757575', fontWeight: 400, fontSize: '15px', mb: 3, textAlign: 'left', alignSelf: 'flex-start' }}>
                      Add and manage users who can help maintain your profile.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                      <Box sx={{
                        width: 54,
                        height: 54,
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}>
                        <InfoOutlinedIcon sx={{ fontSize: 44, color: '#14124B' }} />
                      </Box>
                      <Typography sx={{ color: '#757575', fontWeight: 400, fontSize: '16px', mb: 0.5, textAlign: 'center' }}>No users to see yet.</Typography>
                      <Typography sx={{ color: '#757575', fontWeight: 400, fontSize: '15px', textAlign: 'center' }}>Add a user to get started</Typography>
                    </Box>
                    <Button variant="contained" sx={{ background: '#1344EB', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1.2, boxShadow: 'none', textTransform: 'none', width: '100%' }} onClick={() => { setAddUserModalOpen(true); }}>
                      Add User
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#212121', textAlign: 'left', alignSelf: 'flex-start' }}>Manage Users</Typography>
                    <Typography sx={{ color: '#757575', fontWeight: 400, fontSize: '15px', mb: 3, textAlign: 'center' }}>
                      Add and manage users who can help maintain your profile.
                    </Typography>
                    <Button variant="contained" sx={{ background: '#1344EB', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1.2, boxShadow: 'none', textTransform: 'none', width: '100%', mb: 2 }} onClick={() => { setAddUserModalOpen(true); }}>
                      Add User
                    </Button>
                    <Box sx={{ width: '100%' }}>
                      {manageUsersList.map((user, idx) => (
                        <Box key={user.email + idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1.5, gap: 4 }}>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#212121', display: 'inline-block', mr: 1 }}>{user.name}</Typography>
                            <Box component="span" sx={{ bgcolor: '#E9F8EF', color: '#22C55E', fontWeight: 500, fontSize: '13px', borderRadius: '6px', px: 1.2, py: 0.2, ml: 0.5, verticalAlign: 'middle' }}>Active</Box>
                            <Typography sx={{ color: '#757575', fontSize: '15px', fontWeight: 400, mt: 0.2 }}>{user.email}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ color: '#757575', fontWeight: 500, fontSize: '15px' }}>{user.role}</Typography>
                            <IconButton size="small" sx={{ color: '#757575' }} onClick={() => { setEditingUserIdx(idx); setEditUserRole(user.role.toLowerCase()); setEditUserModalOpen(true); }}><EditOutlinedIcon fontSize="small" /></IconButton>
                            <IconButton size="small" sx={{ color: '#F04438' }} onClick={() => { setRemovingUserIdx(idx); setRemoveUserModalOpen(true); }}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                {/* Add User Modal */}
                <Dialog open={addUserModalOpen} onClose={() => { setAddUserModalOpen(false); setAddUserEmail(''); setAddUserRole(''); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 0 } }}>
                  <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', pb: 0, pr: 4 }}>
                    Add User
                    <IconButton aria-label="close" onClick={() => { setAddUserModalOpen(false); setAddUserEmail(''); setAddUserRole(''); }} sx={{ position: 'absolute', right: 12, top: 12, color: '#757575' }}>
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 1, pb: 0 }}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={addUserEmail}
                      onChange={e => setAddUserEmail(e.target.value)}
                      label="Enter user email"
                      sx={{
                        mt: 2,
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: 500,
                          background: '#fff',
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#212121',
                        },
                      }}
                      InputProps={{ style: { fontSize: '16px', fontWeight: 500, color: '#212121' } }}
                    />
                    <TextField
                      select
                      variant="outlined"
                      fullWidth
                      value={addUserRole}
                      onChange={e => setAddUserRole(e.target.value)}
                      label="Select user role"
                      // SelectProps={{ displayEmpty: true }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: 500,
                          background: '#fff',
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#212121',
                        },
                      }}
                      InputProps={{ style: { fontSize: '16px', fontWeight: 500, color: '#212121' } }}
                    >
                      <MenuItem value="" disabled style={{ display: 'none' }} />
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="advisor">Advisor</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                    </TextField>
                    {addUserEmail && addUserRole && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', mb: 1, color: '#212121' }}>Permissions</Typography>
                        <Box>
                          {[1,2,3,4].map((_, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ color: '#22C55E', fontSize: 22, mr: 1 }} />
                              <Typography sx={{ color: '#212121', fontSize: '15px', fontWeight: 500 }}>Permission</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ justifyContent: 'flex-end', pb: 2, pt: 0, pr: 2 }}>
                    <Button onClick={() => { setAddUserModalOpen(false); setAddUserEmail(''); setAddUserRole(''); }} sx={{ color: '#212121', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, background: '#fff', boxShadow: 'none', border: '1px solid #e0e0e0', mr: 1.5 }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ background: addUserEmail && addUserRole ? '#1344EB' : '#e0e0e0', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, boxShadow: 'none', textTransform: 'none', color: addUserEmail && addUserRole ? '#fff' : '#bdbdbd' }}
                      disabled={!(addUserEmail && addUserRole)}
                      onClick={handleAddUser}
                    >
                      Add
                    </Button>
                  </DialogActions>
                </Dialog>
                {/* Edit User Modal */}
                <Dialog open={editUserModalOpen} onClose={() => { setEditUserModalOpen(false); setEditingUserIdx(null); setEditUserRole(''); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 0 } }}>
                  <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', pb: 0, pr: 4 }}>
                    Edit User
                    <IconButton aria-label="close" onClick={() => { setEditUserModalOpen(false); setEditingUserIdx(null); setEditUserRole(''); }} sx={{ position: 'absolute', right: 12, top: 12, color: '#757575' }}>
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 1, pb: 0 }}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={editingUserIdx !== null ? manageUsersList[editingUserIdx]?.email : ''}
                      label="Enter user email"
                      disabled
                      sx={{
                        mt: 2,
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: 500,
                          background: '#fff',
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#212121',
                        },
                      }}
                      InputProps={{ style: { fontSize: '16px', fontWeight: 500, color: '#212121' } }}
                    />
                    <TextField
                      select
                      variant="outlined"
                      fullWidth
                      value={editUserRole}
                      onChange={e => setEditUserRole(e.target.value)}
                      label="Select user role"
                      SelectProps={{ displayEmpty: true }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: 500,
                          background: '#fff',
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1344EB',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#212121',
                        },
                      }}
                      InputProps={{ style: { fontSize: '16px', fontWeight: 500, color: '#212121' } }}
                    >
                      <MenuItem value="" disabled style={{ display: 'none' }} />
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="advisor">Advisor</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                    </TextField>
                    {editUserRole && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', mb: 1, color: '#212121' }}>Permissions</Typography>
                        <Box>
                          {[1,2,3,4].map((_, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ color: '#22C55E', fontSize: 22, mr: 1 }} />
                              <Typography sx={{ color: '#212121', fontSize: '15px', fontWeight: 500 }}>Permission</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ justifyContent: 'flex-end', pb: 2, pt: 0, pr: 2 }}>
                    <Button onClick={() => { setEditUserModalOpen(false); setEditingUserIdx(null); setEditUserRole(''); }} sx={{ color: '#212121', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, background: '#fff', boxShadow: 'none', border: '1px solid #e0e0e0', mr: 1.5 }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ background: editUserRole ? '#1344EB' : '#e0e0e0', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, boxShadow: 'none', textTransform: 'none', color: editUserRole ? '#fff' : '#bdbdbd' }}
                      disabled={!editUserRole}
                      onClick={handleUpdateUser}
                    >
                      Update
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}
          </Box>
        )}
      </div>

      {/* Modal for Send Invite Link */}
      <Dialog open={inviteModalOpen} onClose={handleCloseInviteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 0 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', pb: 0, pr: 4 }}>
          Send Prime Link
          <IconButton aria-label="close" onClick={handleCloseInviteModal} disabled={isLoading} sx={{ position: 'absolute', right: 12, top: 12, color: '#757575' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Formik
          initialValues={{
            inviteEmail: '',
            accountType: '',
          }}
          validationSchema={InviteSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              await handleSendLink(values.inviteEmail, values.accountType);
            } catch (error) {
              console.error('Form submission error:', error);
            } finally {
              setSubmitting(false);
              resetForm();
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent sx={{ pt: 1, pb: 0, position: 'relative' }}>
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                      borderRadius: '8px',
                    }}
                  >
                    <Loader />
                  </Box>
                )}
                <Typography sx={{ color: '#757575', fontSize: '15px', mb: 2, mt: 1 }}>
                  Enter the investor's email below to send them a link to Prime, where they can view their NAV fund information.
                </Typography>
                <TextField
                  fullWidth
                  name="inviteEmail"
                  label="Enter investor's email"
                  type="email"
                  value={values.inviteEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.inviteEmail && Boolean(errors.inviteEmail)}
                  helperText={touched.inviteEmail && errors.inviteEmail}
                  placeholder="example@company.com"
                  disabled={isLoading}
                  sx={{
                    mt: 1,
                    mb: 1.2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 500,
                      background: '#fff',
                      padding: '0',
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-error': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: touched.inviteEmail && errors.inviteEmail ? '#d32f2f' : '#e0e0e0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: touched.inviteEmail && errors.inviteEmail ? '#d32f2f' : '#1976d2',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#212121',
                      padding: '14.5px 12px',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: 0,
                      fontSize: '13px',
                      fontWeight: 500,
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      fontSize: '15px',
                      color: touched.inviteEmail && errors.inviteEmail ? '#d32f2f' : '#bdbdbd',
                      fontWeight: 400,
                      padding: '0 4px',
                    }
                  }}
                  InputProps={{ 
                    style: { fontSize: '16px', fontWeight: 500, color: '#212121', padding: '0' },
                    autoComplete: 'email'
                  }}
                />
                <TextField
                  select
                  fullWidth
                  name="accountType"
                  label="Select Account Type"
                  value={values.accountType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountType && Boolean(errors.accountType)}
                  helperText={touched.accountType && errors.accountType}
                  disabled={isLoading}
                  sx={{
                    mb: 1.2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 500,
                      background: '#fff',
                      borderColor: '#1976d2',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#212121',
                    },
                  }}
                  InputProps={{ style: { fontSize: '16px', fontWeight: 500, color: '#212121' } }}
                >
                  <MenuItem value="" disabled style={{ display: 'none' }} />
                  <MenuItem value="Individual">Individual</MenuItem>
                  <MenuItem value="Corporate">Corporate</MenuItem>
                </TextField>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'flex-end', pb: 2, pt: 0, pr: 2 }}>
                <Button 
                  onClick={handleCloseInviteModal} 
                  disabled={isLoading}
                  sx={{ color: '#212121', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, background: '#fff', boxShadow: 'none', border: '1px solid #e0e0e0', mr: 1.5 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{ 
                    background: isLoading ? '#e0e0e0' : '#1344EB', 
                    fontWeight: 500, 
                    fontSize: '16px', 
                    borderRadius: '8px', 
                    px: 4, 
                    py: 1, 
                    boxShadow: 'none', 
                    textTransform: 'none',
                    color: isLoading ? '#bdbdbd' : '#fff'
                  }}
                  type="submit"
                  disabled={isSubmitting || isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Link'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Remove User Modal */}
      <Dialog open={removeUserModalOpen} onClose={() => { setRemoveUserModalOpen(false); setRemovingUserIdx(null); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 0 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', pb: 0, pr: 4 }}>
          Remove user
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0 }}>
          <Typography sx={{ color: '#757575', fontSize: '15px', mb: 2, mt: 1 }}>
            You can not undo this action.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', pb: 2, pt: 0, pr: 2 }}>
          <Button onClick={() => { setRemoveUserModalOpen(false); setRemovingUserIdx(null); }} sx={{ color: '#212121', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, background: '#fff', boxShadow: 'none', border: '1px solid #e0e0e0', mr: 1.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#B42318', fontWeight: 500, fontSize: '16px', borderRadius: '8px', px: 4, py: 1, boxShadow: 'none', textTransform: 'none', color: '#fff' }}
            onClick={() => {
              if (removingUserIdx !== null && manageUsersInvestor) {
                const newList = manageUsersList.filter((_, i) => i !== removingUserIdx);
                setManageUsersList(newList);
                setInvestorUsersMap(prev => ({ ...prev, [manageUsersInvestor.email]: newList }));
              }
              setRemoveUserModalOpen(false);
              setRemovingUserIdx(null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FundDetails; 